// =====================================================================
//  Profil d'envoi d'un client (onglet « Paramètres Mail » de la fiche client)
//  ---------------------------------------------------------------------
//  Fournisseur + utilisateur Tipimail du client + mot de passe de ce
//  compte (le même couple sert à l'API et au serveur SMTP Tipimail ; la
//  clé d'API, elle, est dans la configuration de WEB_API). Le mot de passe
//  est en ÉCRITURE SEULE : l'API ne le renvoie jamais, on affiche seulement
//  qu'il existe. Champ laissé vide à l'enregistrement = inchangé.
//  Url / Port ne sont pas utilisés par le fournisseur Tipimail : non
//  affichés, mais conservés tels quels à l'enregistrement.
// =====================================================================

import {IDisposableLike} from "../../Tools/Disposable/IDisposableLike";
import {IProfilMailModel} from "../../Model/Client/ProfilMalModel.js";
import {ProfilMail} from "../../Controller/Client/ProfilMail.js";
import {KendoToolBarHandler} from "../../Tools/Misc.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";

const PROVIDERS = [{text: "Tipimail", value: "tipimail"}];

export class ProfilMailEditor implements IDisposableLike {
    readonly #jqMainContainer: JQuery;
    readonly #clientId: string;
    #jqContainer: JQuery | undefined;
    #jqStatus: JQuery | undefined;
    #jqFormContainer: JQuery | undefined;
    #kForm: kendo.ui.Form | undefined;
    #kToolBar: kendo.ui.ToolBar | undefined;
    #current: IProfilMailModel | null = null;
    #edited: IProfilMailModel = {};
    #isValid = false;

    constructor(options: IProfilMailEditorOptions) {
        this.#jqMainContainer = options.jqMainContainer;
        this.#clientId = options.clientId;
    }

    public Init = async () => {
        this.#jqMainContainer.empty();
        this.#buildHtml();
        this.#buildToolbar();
        await this.#load();
    };

    #buildHtml = () => {
        this.#jqContainer = $("<div></div>")
            .css({display: "flex", flexDirection: "column", gap: "8px", height: "100%", width: "100%"})
            .appendTo(this.#jqMainContainer);

        $("<p></p>").css({margin: "8px 0 0 0", maxWidth: "720px"})
            .text("Compte d'envoi du client chez le fournisseur (utilisateur et mot de passe Tipimail, aussi utilisés pour le serveur SMTP). " +
                "Sans profil d'envoi, aucun mail de ce client ne peut partir. " +
                "Le mot de passe n'est jamais réaffiché : laissez le champ vide pour conserver celui enregistré.")
            .appendTo(this.#jqContainer);

        this.#jqStatus = $("<p></p>").css({margin: 0, fontWeight: "bold"}).appendTo(this.#jqContainer);
        this.#jqFormContainer = $("<div></div>").css({width: "50%", minWidth: "420px"}).appendTo(this.#jqContainer);

        const toolWrap = $("<div></div>").css({width: "100%", marginTop: "auto"}).appendTo(this.#jqContainer);
        this.#kToolBar = $("<div></div>").appendTo(toolWrap).kendoToolBar({
            size: "small",
            items: [],
        }).data("kendoToolBar");
    };

    #buildToolbar = () => {
        this.#kToolBar.add({
            type: "button",
            text: "Enregistrer",
            icon: "save",
            themeColor: "success",
            click: (e: any) => KendoToolBarHandler.AsyncToolbarHandle(e.target, this.#save),
        });
    };

    #load = async () => {
        this.#current = await ProfilMail.Read(this.#clientId);
        this.#edited = {
            Primarykey: this.#clientId,
            Nom: this.#current?.Nom ?? PROVIDERS[0].value,
            Login: this.#current?.Login ?? "",
            Password: "",
            Url: this.#current?.Url,
            Port: this.#current?.Port,
        };
        this.#renderStatus();
        this.#buildForm();
    };

    #renderStatus = () => {
        const c = this.#current;
        if (!c)
            this.#jqStatus.css({color: "#c62828"}).text("Aucun profil d'envoi : ce client ne peut pas envoyer de mail.");
        else if (!c.HasPassword)
            this.#jqStatus.css({color: "#e65100"}).text("Profil incomplet : aucun mot de passe enregistré.");
        else
            this.#jqStatus.css({color: "#2e7d32"})
                .text(`Profil configuré (${c.Nom}, utilisateur ${c.Login}, mot de passe enregistré).`);
    };

    #buildForm = () => {
        this.#kForm?.destroy();
        this.#jqFormContainer.empty();
        const hasPassword = !!this.#current?.HasPassword;

        this.#kForm = $("<form></form>").appendTo(this.#jqFormContainer).kendoForm({
            formData: {...this.#edited},
            orientation: "horizontal",
            size: "small",
            items: [
                {
                    field: "Nom",
                    label: "Fournisseur",
                    editor: "DropDownList",
                    editorOptions: {
                        dataSource: PROVIDERS,
                        dataTextField: "text",
                        dataValueField: "value",
                        valuePrimitive: true,
                    },
                    validation: {required: true},
                },
                {
                    field: "Login",
                    label: "Utilisateur API",
                    hint: "Utilisateur Tipimail du client (le même que pour le serveur SMTP Tipimail).",
                    attributes: {autocomplete: "off"},
                    validation: {required: true},
                },
                {
                    field: "Password",
                    label: "Mot de passe",
                    hint: hasPassword
                        ? "Mot de passe enregistré (non réaffiché). Laissez vide pour le conserver."
                        : "Mot de passe de l'utilisateur Tipimail du client. Saisie ici uniquement : ne le transmettez jamais par mail ou à un assistant IA.",
                    attributes: {type: "password", autocomplete: "new-password"},
                    validation: {required: !hasPassword},
                },
            ],
            change: (e: kendo.ui.FormChangeEvent) => {
                (this.#edited as any)[e.field] = e.value;
            },
            validate: (e: kendo.ui.FormValidateEvent) => {
                this.#isValid = e.valid;
            },
        }).data("kendoForm");

        this.#kForm.element.find(".k-form-buttons").remove();
    };

    #save = async () => {
        // Form.validate() ne renvoie rien : le résultat arrive par l'événement validate.
        this.#kForm.validate();
        if (!this.#isValid)
            return;

        const saved = await ProfilMail.Upsert({...this.#edited, Primarykey: this.#clientId});
        if (!saved)
            return;

        NotificationHelper.Instance.success("Profil d'envoi enregistré.");
        // Recharge depuis l'API : efface la clé saisie et met à jour l'état affiché.
        await this.#load();
    };

    public Dispose = () => {
        this.#kForm?.destroy();
        this.#kToolBar?.destroy();
        this.#jqMainContainer.empty();
    };
}

export interface IProfilMailEditorOptions {
    jqMainContainer: JQuery;
    clientId: string;
}
